
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import PostsList from './PostsList';
import PostEditor from './PostEditor';
import AuthorEditor from './AuthorEditor';
import BatchUpload from './BatchUpload';
import SubmissionsList from './SubmissionsList';

interface Post {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  category: string;
  featured: boolean;
  published: boolean;
  rating: number;
  cover_image: string;
  created_at: string;
  updated_at: string;
}

interface Author {
  id: string;
  name: string;
  location: string;
  bio: string;
  recent_work: string;
  quote: string;
  achievements: string[];
  image_url: string;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

interface Submission {
  id: string;
  name: string;
  email: string;
  submission_type: string;
  title: string;
  content: string;
  previous_publications: string;
  bio: string;
  status: string;
  created_at: string;
}

export default function AdminDashboard() {
  const [currentView, setCurrentView] = useState<'list' | 'editor' | 'author' | 'batch' | 'submissions'>('list');
  const [posts, setPosts] = useState<Post[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [featuredAuthor, setFeaturedAuthor] = useState<Author | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
    fetchAuthors();
    fetchSubmissions();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching posts:', error);
      } else {
        setPosts(data || []);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthors = async () => {
    try {
      const { data, error } = await supabase
        .from('authors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching authors:', error);
      } else {
        setAuthors(data || []);
        const featured = data?.find(author => author.is_featured);
        setFeaturedAuthor(featured || null);
      }
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching submissions:', error);
      } else {
        setSubmissions(data || []);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setCurrentView('editor');
  };

  const handleEditPost = (post: Post) => {
    setEditingPost(post);
    setCurrentView('editor');
  };

  const handleDeletePost = async (postId: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        const { error } = await supabase
          .from('posts')
          .delete()
          .eq('id', postId);

        if (error) {
          console.error('Error deleting post:', error);
        } else {
          fetchPosts();
        }
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleSubmissionStatusChange = async (submissionId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', submissionId);

      if (error) {
        console.error('Error updating submission status:', error);
      } else {
        fetchSubmissions();
      }
    } catch (error) {
      console.error('Error updating submission status:', error);
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      try {
        const { error } = await supabase
          .from('submissions')
          .delete()
          .eq('id', submissionId);

        if (error) {
          console.error('Error deleting submission:', error);
        } else {
          fetchSubmissions();
        }
      } catch (error) {
        console.error('Error deleting submission:', error);
      }
    }
  };

  const handlePostSaved = () => {
    fetchPosts();
    setCurrentView('list');
  };

  const handleAuthorSaved = () => {
    fetchAuthors();
    setCurrentView('list');
  };

  const handleEditAuthor = () => {
    setCurrentView('author');
  };

  const handleBatchUpload = () => {
    setCurrentView('batch');
  };

  const handleBatchComplete = () => {
    fetchPosts();
    setCurrentView('list');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const pendingSubmissions = submissions.filter(s => s.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setCurrentView('list')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer ${
                  currentView === 'list'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Posts List
              </button>
              <button
                onClick={() => setCurrentView('submissions')}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer relative ${
                  currentView === 'submissions'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Submissions
                {pendingSubmissions > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingSubmissions}
                  </span>
                )}
              </button>
              <button
                onClick={handleCreatePost}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Create Post
              </button>
              <button
                onClick={handleBatchUpload}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Batch Upload
              </button>
              <button
                onClick={handleEditAuthor}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Edit Featured Author
              </button>
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {currentView === 'list' ? (
          <PostsList
            posts={posts}
            onEdit={handleEditPost}
            onDelete={handleDeletePost}
          />
        ) : currentView === 'submissions' ? (
          <SubmissionsList
            submissions={submissions}
            onStatusChange={handleSubmissionStatusChange}
            onDelete={handleDeleteSubmission}
          />
        ) : currentView === 'editor' ? (
          <PostEditor
            post={editingPost}
            onSave={handlePostSaved}
            onCancel={() => setCurrentView('list')}
          />
        ) : currentView === 'batch' ? (
          <BatchUpload
            onComplete={handleBatchComplete}
            onCancel={() => setCurrentView('list')}
          />
        ) : (
          <AuthorEditor
            author={featuredAuthor}
            onSave={handleAuthorSaved}
            onCancel={() => setCurrentView('list')}
          />
        )}
      </main>
    </div>
  );
}
